import { mapState } from 'vuex';
import ApiService from '@/services/apiService';
import cloneDeep from 'lodash/cloneDeep';
import uniqWith from 'lodash/uniqWith';
import moment from 'moment';
export default {
  name: 'ViewDigitalTwinDigitalTwinStatesOverview',
  data: function () {
    return {
      rootItems: [],
      twinItemMapping: {},
      sidebarItems: [],
      selectedTwin: null,
      twinStates: [],
      statesTableOptions: {
        sortBy: ['combinedValue'],
        sortDesc: [true]
      },
      statesTableFooterProps: {
        itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
        itemsPerPageOptions: [15, 25, 50, {
          text: this.$i18n_t('Common.all'),
          value: -1
        }]
      },
      statesTableHeaders: [{
        sortable: false,
        text: '',
        value: 'properties.faIcon'
      }, {
        sortable: true,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.states.title'),
        value: 'properties.title'
      }, {
        sortable: true,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.states.value'),
        value: 'combinedValue',
        align: 'right'
      }, {
        sortable: true,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.states.unit'),
        value: 'unit'
      }, {
        sortable: true,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.states.lastUpdate'),
        value: 'stateTime'
      }]
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color']),
    aggregatedDTwinStates() {
      return this.twinStates.filter(s => this.isStateAggregation(s));
    },
    nonAggregatedDTwinStates() {
      return this.twinStates.filter(s => !this.isStateAggregation(s));
    },
    maxHierarchyOrder() {
      return this.sidebarItems.reduce((a, b) => Math.max(a, b.hierarchyOrder), -1);
    }
  },
  watch: {
    statesTableOptions: {
      // eslint-disable-next-line no-unused-vars
      handler(newValue) {
        let currentSort = this.statesTableOptions.sortBy;
        // cannot sort by the display-date, since that does not give a valid string-order
        if (currentSort[0] === 'stateTime') {
          let newSorting = cloneDeep(newValue);
          newSorting.sortBy = ['value.time'];
          this.statesTableOptions = newSorting;
        }
      },
      deep: true
    }
  },
  mounted() {
    this.fetchChildren();
  },
  methods: {
    localizeDate(a) {
      if (a) {
        moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
        return moment(a).format('L LTS');
      }
      return '-';
    },
    relativeTime(date) {
      if (date) {
        moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
        return moment(date).fromNow();
      }
      return '-';
    },
    goToTwin(id) {
      window.open(`#/digitaltwin-mgmt/${id}`, '_blank');
    },
    isStateAggregation(item) {
      if (item.properties.unit === 'State-Aggregation') {
        if (`${item.value.value}`.trim() === '') {
          return true;
        }
        if (typeof item.value.value !== 'object') {
          const jsonObj = JSON.parse(item.value.value);
          return jsonObj;
        }
        return item.value.value;
      }
      return false;
    },
    selectDigitalTwin(dtwin) {
      // if a root-item is selected again, show all root-items again
      if (dtwin && this.selectedTwin && dtwin.id === this.selectedTwin.id) {
        if (!this.selectedTwin.parentId || !this.twinItemMapping[this.selectedTwin.parentId]) {
          this.selectedTwin = null;
          this.sidebarItems = cloneDeep(this.rootItems);
          return;
        } else if (this.childCount(this.selectedTwin)) {
          // select the parent, if this is not a leaf
          this.selectedTwin = cloneDeep(this.twinItemMapping[this.selectedTwin.parentId]);
        }
      } else {
        this.selectedTwin = cloneDeep(this.twinItemMapping[dtwin.id]);
      }
      if (this.childCount(this.selectedTwin)) {
        this.fetchChildren(this.selectedTwin.id, 0);
      } else {
        this.buildDisplayHierarchy();
      }
    },
    childCount(twin) {
      return ((twin || {}).children || []).length || (twin || {}).childCount || 0;
    },
    fetchChildren(parentId, offset) {
      this.treeLoading = true;
      this.$store.state.app.load = 10;
      this.$store.state.app.loading = true;
      return ApiService.digitalTwinFetchChildren(this, parentId, offset).then(data => {
        const dtwins = cloneDeep(data.digitaltwins);
        dtwins.forEach(twin => {
          this.twinItemMapping[twin.id] = twin;
        });
        if (parentId && this.twinItemMapping[parentId]) {
          const parent = this.twinItemMapping[parentId];
          if (!parent.children) {
            parent.children = dtwins;
          } else {
            parent.children = parent.children.concat(dtwins);
          }
          parent.children = uniqWith(parent.children, (a, b) => {
            return a.id === b.id;
          });
          this.buildDisplayHierarchy();
        } else {
          this.sidebarItems = dtwins;
          this.rootItems = dtwins;
        }
        return dtwins;
      }).catch(error => {
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(this.$i18n_t('Common.digitalTwins.errorFindingDTwins'), 'error');
        }
      }).finally(() => {
        this.treeLoading = false;
        this.$store.state.app.loading = false;
      });
    },
    buildDisplayHierarchy() {
      this.selectedTwin = cloneDeep(this.twinItemMapping[this.selectedTwin.id]);
      ApiService.digitalTwinFetchStates(this, this.selectedTwin.id).then(states => {
        states.forEach(state => {
          let value = state.value.value;
          if (!isNaN(parseFloat(value))) {
            value = Math.round(value * 100000) / 100000;
          } else if (typeof value === 'object') {
            value = JSON.stringify(value);
          }
          state.stateTime = state.value.time;
          state.combinedValue = value;
          state.unit = state.properties.unit;
        });
        this.twinStates = states;
      }).catch(error => {
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(this.$i18n_t('Common.digitalTwins.errorFindingDTwins'), 'error');
        }
      });
      const findParents = (dtwinId, list) => {
        const dtwin = this.twinItemMapping[dtwinId];
        if (dtwin.parentId && this.twinItemMapping[dtwin.parentId]) {
          return findParents(dtwin.parentId, [this.twinItemMapping[dtwin.parentId]].concat(list));
        }
        return list;
      };
      const parentItems = cloneDeep(findParents(this.selectedTwin.id, []));
      // as for the last element, do not darken the parent elements any further
      // otherwise there is an ugly color-glitch when the last child is selected
      const darkenOffset = !this.selectedTwin.children || this.selectedTwin.children.length === 0 ? -1 : 0;

      // first show all the parents of this item, getting darker and darker
      parentItems.forEach((parentItem, index) => {
        parentItem.class = `darken-${parentItems.length - index + darkenOffset} white--text`;
        parentItem.hierarchyOrder = index;
      });
      parentItems.push(cloneDeep(this.selectedTwin));
      parentItems[parentItems.length - 1].class = this.selectedTwin.children ? 'darken-0 white--text' : null;
      parentItems[parentItems.length - 1].hierarchyOrder = parentItems.length - 1;
      if (this.selectedTwin.children) {
        const children = cloneDeep(this.selectedTwin.children);
        children.forEach(child => {
          child.hierarchyOrder = parentItems.length;
        });
        this.sidebarItems = parentItems.concat(children);
        return;
      }
      this.sidebarItems = parentItems;
    },
    getTwinTreeIcon(dtwin) {
      const faIcon = `fa-${dtwin.data && dtwin.data.faIcon ? dtwin.data.faIcon : 'dice-d8'}`;
      return !dtwin.parentId ? `fas ${faIcon}` : !dtwin.children || dtwin.children.length === 0 ? `fal ${faIcon}` : `fal ${faIcon}`;
    }
  }
};